import { VAT_RATE } from '../config'

const roundMoney = value => Math.round(Number(value) * 100) / 100

/**
 * `enteredPrice` is what the user typed in the modal.
 * When priceIncludesVat is true, that value is gross (VAT already in it).
 */
export function resolveManualOrderLineVat({
  enteredPrice,
  hasVat,
  priceIncludesVat,
  quantity = 1,
}) {
  const entered = Number(enteredPrice)
  const qty = Number(quantity)
  const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1

  if (!Number.isFinite(entered) || entered < 0) {
    return {
      exVatUnit: 0,
      vatUnit: 0,
      grossUnit: 0,
      exVatLine: 0,
      vatLine: 0,
      grossLine: 0,
    }
  }

  if (!hasVat) {
    const unit = roundMoney(entered)
    return {
      exVatUnit: unit,
      vatUnit: 0,
      grossUnit: unit,
      exVatLine: roundMoney(unit * safeQty),
      vatLine: 0,
      grossLine: roundMoney(unit * safeQty),
    }
  }

  const grossUnit = roundMoney(priceIncludesVat ? entered : entered * VAT_RATE)
  const exVatUnit = roundMoney(grossUnit / VAT_RATE)
  const vatUnit = roundMoney(grossUnit - exVatUnit)

  return {
    exVatUnit,
    vatUnit,
    grossUnit,
    exVatLine: roundMoney(exVatUnit * safeQty),
    vatLine: roundMoney(vatUnit * safeQty),
    grossLine: roundMoney(grossUnit * safeQty),
  }
}

/** Stored OMS unit price is always ex VAT. */
export function resolveStoredExVatUnitPrice({
  enteredPrice,
  hasVat,
  priceIncludesVat,
}) {
  return resolveManualOrderLineVat({
    enteredPrice,
    hasVat,
    priceIncludesVat,
  }).exVatUnit
}

export function sumManualOrderGrossTotal(lines = []) {
  return roundMoney(
    lines.reduce((sum, line) => {
      const breakdown = resolveManualOrderLineVat({
        enteredPrice: line?.OMSPrice,
        hasVat: Boolean(line?.hasVat),
        priceIncludesVat: Boolean(line?.priceIncludesVat),
        quantity: line?.quantity,
      })
      return sum + breakdown.grossLine
    }, 0),
  )
}

export function sumManualOrderExVatTotal(lines = []) {
  return roundMoney(
    lines.reduce((sum, line) => {
      const breakdown = resolveManualOrderLineVat({
        enteredPrice: line?.OMSPrice,
        hasVat: Boolean(line?.hasVat),
        priceIncludesVat: Boolean(line?.priceIncludesVat),
        quantity: line?.quantity,
      })
      return sum + breakdown.exVatLine
    }, 0),
  )
}

export function resolveStoredOrderLineVat({
  exVatUnitPrice,
  hasVat,
  quantity = 1,
}) {
  return resolveManualOrderLineVat({
    enteredPrice: exVatUnitPrice,
    hasVat,
    priceIncludesVat: false,
    quantity,
  })
}

export function getReceiptingLineVatBreakdownCopy({
  exVatUnitPrice,
  hasVat,
  priceIncludesVat,
  quantity = 1,
}) {
  const breakdown = resolveStoredOrderLineVat({
    exVatUnitPrice,
    hasVat,
    quantity,
  })
  const formatPounds = value => `£${roundMoney(value).toFixed(2)}`

  if (!hasVat) {
    return {
      enteredLine: `Unit price: ${formatPounds(breakdown.grossUnit)}`,
      exVatLine: null,
      vatLine: null,
      totalLine: `Total: ${formatPounds(breakdown.grossLine)}`,
    }
  }

  if (priceIncludesVat) {
    return {
      enteredLine: `Receipt price: ${formatPounds(
        breakdown.grossUnit,
      )} (VAT included)`,
      exVatLine: `Ex VAT: ${formatPounds(breakdown.exVatUnit)}`,
      vatLine: `VAT: ${formatPounds(breakdown.vatUnit)}`,
      totalLine: `Total: ${formatPounds(breakdown.grossUnit)}`,
      lineTotalLine:
        quantity > 1
          ? `Line total: ${formatPounds(breakdown.grossLine)}`
          : null,
    }
  }

  return {
    enteredLine: `Unit price: ${formatPounds(breakdown.exVatUnit)} (ex VAT)`,
    exVatLine: `Ex VAT: ${formatPounds(breakdown.exVatUnit)}`,
    vatLine: `VAT: ${formatPounds(breakdown.vatUnit)}`,
    totalLine: `Total: ${formatPounds(breakdown.grossUnit)}`,
    lineTotalLine:
      quantity > 1 ? `Line total: ${formatPounds(breakdown.grossLine)}` : null,
  }
}

/**
 * One line for a catch-weight receipt: "6.94 kg × £11.62 = £80.64 ex VAT".
 */
export function describeCatchWeightReceiptSum({
  weight,
  unitPrice,
  unitLabel = 'kg',
} = {}) {
  const receivedWeight = Number(weight)
  const price = Number(unitPrice)
  if (!Number.isFinite(receivedWeight) || receivedWeight <= 0) return ''
  if (!Number.isFinite(price)) return ''
  const exVat = Math.round(receivedWeight * price * 100) / 100
  const weightText = String(Math.round(receivedWeight * 1000) / 1000)
  const unit = String(unitLabel || 'kg').trim() || 'kg'
  return `${weightText} ${unit} × £${price.toFixed(2)} = £${exVat.toFixed(
    2,
  )} ex VAT`
}

export function sumManualOrderVatTotal(lines = []) {
  return roundMoney(
    lines.reduce((sum, line) => {
      const breakdown = resolveManualOrderLineVat({
        enteredPrice: line?.OMSPrice,
        hasVat: Boolean(line?.hasVat),
        priceIncludesVat: Boolean(line?.priceIncludesVat),
        quantity: line?.quantity,
      })
      return sum + breakdown.vatLine
    }, 0),
  )
}
